import { FC, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Checkbox, ConfigProvider, Spin } from "antd";
import { LoadingOutlined } from "@ant-design/icons";
import { useSelector } from "react-redux";
import { getTheme } from "Store/MultiDomain/MultiDomain.selector";
import { IDiscipline } from "Data/interfaces/Obra/IObraData";
import {
  ConstructionSiteDisciplineLibraryEnum,
  IConstructionSiteDisciplineLibrary,
  IConstructionSiteDisciplineLibraryItem,
  IConstructionSiteDisciplineLibraryRequest,
  ImportedDisciplineLibraryTypeEnum,
} from "Data/interfaces/Obra/IDisciplineLibraries";
import Button from "Components/UI/Button";
import { Icon, IconName } from "Components/UI";
import Modal from "Components/UI/Modal";
import Ellipsis from "Components/UI/Ellipsis";
import { GetAvatar } from "Utils/generateThumbnail";
import {
  getDisciplineDisplayAcronym,
  getDisciplineDisplayName,
} from "../../disciplines.helper";
import {
  Body,
  BodyContent,
  Children,
  Container,
  Content,
  Footer,
  HeaderContent,
  Hr,
  Left,
  Main,
  MainContent,
  MainContentTop,
  Section,
  Title,
  Top,
} from "./styles";

interface OptionsDefault {
  title: string;
  children: IConstructionSiteDisciplineLibrary[];
}

export interface ISelectedItem {
  id: number;
  acronym: string;
  name: string;
  icon: IconName;
}

interface ILibrary {
  visible: boolean;
  csId?: number;
  loading?: boolean;
  listLoading?: boolean;
  libraries: IConstructionSiteDisciplineLibrary[];
  disciplines: IDiscipline[];
  onSubmit?: (values: IConstructionSiteDisciplineLibraryRequest) => void;
  onImport?: (items: ISelectedItem[]) => void;
  onCancel: () => void;
}

const iconNameTypeEnum: Record<ConstructionSiteDisciplineLibraryEnum, IconName> = {
  [ConstructionSiteDisciplineLibraryEnum.Standard]: 'coordly',
  [ConstructionSiteDisciplineLibraryEnum.MarketStandard]: 'abnt',
  [ConstructionSiteDisciplineLibraryEnum.ConstructionSite]: 'menuContexto',
};

const Library: FC<ILibrary> = ({
  visible,
  csId,
  loading,
  listLoading,
  libraries,
  disciplines,
  onSubmit,
  onImport,
  onCancel,
}) => {
  const { t } = useTranslation('editObra');
  const theme = useSelector(getTheme);

  const [active, setActive] = useState(0);
  const [activeType, setActiveType] = useState<ConstructionSiteDisciplineLibraryEnum | null>(null);
  const [selecteds, setSelecteds] = useState<ISelectedItem[]>([]);

  const options = useMemo(() => {
    const data: OptionsDefault[] = [];

    if (libraries && libraries.length > 0) {
      const standardLibraries = libraries.filter(lib => {
        return lib.Type === ConstructionSiteDisciplineLibraryEnum.Standard;
      });
      const marketLibraries = libraries.filter(lib => {
        return lib.Type === ConstructionSiteDisciplineLibraryEnum.MarketStandard;
      });
      const constructionSiteLibraries = libraries.filter(lib => {
        return lib.Type === ConstructionSiteDisciplineLibraryEnum.ConstructionSite &&
          lib.ConstructionSiteDisciplineLibraryId !== csId;
      });

      if (standardLibraries.length > 0) {
        const opt: OptionsDefault = {
          title: t('disciplines.selectPattern'),
          children: []
        };

        for (const lib of standardLibraries) {
          opt.children.push(lib);
        }

        data.push(opt);
      }

      if (marketLibraries.length > 0) {
        const opt: OptionsDefault = {
          title: t('stagesPage.marketPatterns'),
          children: []
        };

        for (const lib of marketLibraries) {
          opt.children.push(lib);
        }

        data.push(opt);
      }

      if (constructionSiteLibraries.length > 0) {
        const opt: OptionsDefault = {
          title: t('stagesPage.obraPatterns'),
          children: []
        };

        for (const lib of constructionSiteLibraries) {
          opt.children.push(lib);
        }

        data.push(opt);
      }
    }

    return data;
  }, [libraries, csId, t]);

  const optionActive = useMemo(() => {
    return libraries.find(opt => {
      return opt.ConstructionSiteDisciplineLibraryId === active && opt.Type === activeType;
    });
  }, [active, activeType, libraries]);

  const optionActiveChildren = useMemo(() => {
    let dataDefault: IConstructionSiteDisciplineLibraryItem[] = [];

    if (optionActive && optionActive?.Items && optionActive.Items?.length > 0) {
      dataDefault = optionActive.Items.sort((a, b) => a.Order - b.Order);
    }

    const allOpts: ISelectedItem[] = [];
    for (const item of dataDefault) {
      allOpts.push({
        id: item.ConstructionSiteDisciplineLibraryItemId,
        acronym: item.Acronym,
        name: item.Name,
        icon: item.Icon || 'menuContexto',
      });
    }

    return {
      data: dataDefault,
      dataChildIds: allOpts,
      count: allOpts.length,
    };
  }, [optionActive]);

  const isAlreadyPresent = (opt: { Acronym?: string; Name?: string }) => (
    disciplines.some(discipline => (
      getDisciplineDisplayAcronym(discipline)?.toLowerCase() === opt.Acronym?.toLowerCase() ||
      getDisciplineDisplayName(discipline)?.toLowerCase() === opt.Name?.toLowerCase()
    ))
  );

  const disabledImport = useMemo(() => {
    return loading || optionActiveChildren.data.every(opt => isAlreadyPresent(opt));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, optionActiveChildren.data, disciplines]);

  const handleSubmit = (type: ImportedDisciplineLibraryTypeEnum) => {
    if (active === 0 || selecteds.length === 0) return;

    const filteredSelecteds = selecteds.filter(item => !isAlreadyPresent({ Acronym: item.acronym, Name: item.name }));

    if (filteredSelecteds.length === 0) return;

    if (onImport) {
      onImport(filteredSelecteds);
      return;
    }

    if (onSubmit && csId) {
      const request: IConstructionSiteDisciplineLibraryRequest = {
        constructionSiteId: csId,
        constructionSiteDisciplineLibraryId: active,
        constructionSiteDisciplineLibraryItems: filteredSelecteds.map(item => item.id),
        type,
      };
      onSubmit(request);
    }
  }

  const onCheck = (value: ISelectedItem) => {
    setSelecteds(prev => {
      if (prev.some(item => item.id === value.id)) {
        return prev.filter(item => item.id !== value.id);
      }

      return [...prev, value];
    })
  };

  const onCheckAll = () => {
    setSelecteds(prev => {
      if (prev.length === optionActiveChildren.count) {
        const exists = optionActiveChildren.dataChildIds.filter(item => isAlreadyPresent({ Acronym: item.acronym, Name: item.name }));

        return [...exists];
      }

      return optionActiveChildren.dataChildIds;
    });
  };

  useEffect(() => {
    if (libraries && libraries.length > 0) {
      const standardLibrary = libraries.find(lib => {
        return lib.Type === ConstructionSiteDisciplineLibraryEnum.Standard;
      });
      const current = standardLibrary || libraries[0];

      setActive(current.ConstructionSiteDisciplineLibraryId);
      setActiveType(current.Type);
    }
  }, [libraries]);

  useEffect(() => {
    if (active) {
      setSelecteds([]);
      onCheckAll();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active, activeType]);

  return (
    <Modal
      visible={visible}
      width={750}
      footerButtons={null}
    >
      <Container>
        <Title>
          <span className="textTitle">{t('disciplines.libraryTitle')}</span>
          <Icon
            icon="cancelar"
            customSize={12}
            className="iconTitle"
            onClick={onCancel}
          />
        </Title>

        <Content>
          <Left>
            {options.map((opt, index) => (
              <Section key={opt.title}>
                <span className="title">{opt.title}</span>
                {opt.children.map(item => (
                  <Children
                    key={item.ConstructionSiteDisciplineLibraryId}
                    isactive={active === item.ConstructionSiteDisciplineLibraryId && activeType === item.Type}
                    onClick={() => {
                      setActive(item.ConstructionSiteDisciplineLibraryId);
                      setActiveType(item.Type);
                    }}
                  >
                    {(item.IconName ||
                      item.Type === ConstructionSiteDisciplineLibraryEnum.Standard ||
                      item.Type === ConstructionSiteDisciplineLibraryEnum.MarketStandard) && (
                        <Icon
                          icon={item.IconName || iconNameTypeEnum[item.Type]}
                        />
                      )}
                    {item.Type === ConstructionSiteDisciplineLibraryEnum.ConstructionSite && (
                      <GetAvatar
                        name={item.Name}
                        shape="circle"
                        size={20}
                        thumbType="mid"
                      />
                    )}
                    <Ellipsis
                      phrase={item.Name}
                      maxLength={17}
                      className="titleChildren"
                    />
                  </Children>
                ))}
                {index !== options.length - 1 && <Hr />}
              </Section>
            ))}
          </Left>

          <Main>
            {optionActive && (
              <Top>
                <span className="title">{optionActive.Name}</span>
                <Ellipsis
                  phrase={optionActive?.Description || ''}
                  maxLength={147}
                  className="description"
                />
              </Top>
            )}
            <MainContent>
              <ConfigProvider theme={{
                token: {
                  colorBgContainerDisabled: `color-mix(in srgb, ${theme.colors.primary.primaryOutline}, ${theme.colors.surface.surface})`,
                  colorTextDisabled: theme.colors.primary.onPrimary,
                  colorPrimary: theme.colors.primary.primary
                }
              }}>
                <MainContentTop>
                  <HeaderContent>
                    <span className="checkbox">
                      <Checkbox
                        checked={selecteds.length === optionActiveChildren.count}
                        indeterminate={
                          selecteds.length > 0 &&
                          selecteds.length !== optionActiveChildren.count
                        }
                        disabled={listLoading}
                        onClick={onCheckAll}
                      />
                    </span>
                    <span className="columnIcon">{t('disciplines.columnIcon')}</span>
                    <span className="columnSigla">{t('disciplines.columnAcronym')}</span>
                    <span className="columnDiscipline">{t('disciplines.columnDiscipline')}</span>
                  </HeaderContent>
                  <Body>
                    <BodyContent>
                      {optionActiveChildren.data.map(opt => {
                        const isActive = selecteds.some(item => {
                          return item.id === opt.ConstructionSiteDisciplineLibraryItemId;
                        });
                        const isDisabled = listLoading || isAlreadyPresent(opt);

                        return (
                          <span key={opt.ConstructionSiteDisciplineLibraryItemId} className="row">
                            <span className="checkbox">
                              <Checkbox
                                checked={isActive}
                                disabled={isDisabled}
                                onClick={() => onCheck({
                                  id: opt.ConstructionSiteDisciplineLibraryItemId,
                                  acronym: opt.Acronym,
                                  name: opt.Name,
                                  icon: opt.Icon || 'menuContexto',
                                })}
                              />
                            </span>
                            <span className={`rightRow ${(!isDisabled && isActive) ? 'rightRowActive' : ''}`}>
                              <span className="columnIcon">
                                <Icon icon={opt.Icon || 'menuContexto'} customSize={16} className="rowIcon" />
                              </span>
                              <span className="columnSigla">{opt.Acronym}</span>
                              <span className="columnDiscipline">{opt.Name}</span>
                            </span>
                          </span>
                        )
                      })}
                    </BodyContent>
                  </Body>
                </MainContentTop>
                <Footer>
                  <span className="textLeft">
                    {t('disciplines.selectedCount', { count: selecteds.length })}
                  </span>
                  <Button
                    type="primary"
                    className="btnFooter"
                    disabled={disabledImport}
                    onClick={() => handleSubmit(optionActive?.Type === ConstructionSiteDisciplineLibraryEnum.ConstructionSite
                      ? ImportedDisciplineLibraryTypeEnum.ConstructionSite
                      : ImportedDisciplineLibraryTypeEnum.Predefined
                    )}
                  >
                    {loading && (
                      <Spin
                        indicator={
                          <LoadingOutlined
                            color="white"
                            style={{ fontSize: 12, color: 'white', marginRight: 6 }}
                          />
                        }
                      />
                    )}
                    {loading ? t('disciplines.importing') : t('disciplines.importButton')}
                  </Button>
                </Footer>
              </ConfigProvider>
            </MainContent>
          </Main>
        </Content>
      </Container>
    </Modal>
  )
}

export default Library;
